import moment from 'moment-timezone';

import { Invoice, InvoicePaymentStatus, InvoiceVolumeQuality, VatInfo } from '@models/invoice/invoice.model';
import { Utils } from '@utils/utils';

import { ApiAdapter } from './adapter';
import { DeliveryPointAdapter } from './delivery-point.adapter';
import { InvoiceItemAdapter } from './invoice-item.adapter';
import { UserAdapter } from './user.adapter';

export class InvoiceAdapter extends ApiAdapter<Invoice> {
  private deliveryPointAdapter = new DeliveryPointAdapter();
  private invoiceItemAdapter = new InvoiceItemAdapter();
  private userAdapter = new UserAdapter();

  get(json): Invoice {
    const validatedBy = typeof json['validated_by'] === 'object' ? json['validated_by'] : null;
    const userData = json['generated_by'] ? { okta_login: json['generated_by'] } : validatedBy;
    const invoice = new Invoice({
      id: json['invoice_id'],
      month: moment.tz(json['month'], moment.tz.guess()),
      contractId: json['contract_id'],
      currency: json['currency'] || 'EUR',
      deliveryPoint: this.deliveryPointAdapter.get({
        ...json,
        label_engie: json['site_label_engie'],
        label_client: json['site_label_client'],
      }),
      versionDate: moment.tz(json['version_date'], moment.tz.guess()),
      state: json['state'],
      generatedBy: userData ? this.userAdapter.get(userData) : null,
      items: this.invoiceItemAdapter.list(json['details_merged']),
      total: json['vat_included_total'],
      totalVatExcluded: json['vat_excluded_total'],
      volume: json['volume'],
      inputs: json['inputs'],
      settings: {
        showVolume: json['settings'] ? json['settings']['display_volume'] : true,
      },
      sap: {
        editionDate: moment.tz(json['edition_date'], moment.tz.guess()),
        invoiceNumber: json['sap_number'],
        isPublishToSap: false,
        creditNumber: json['sap_credit_number'],
      },
      wattsonAsAFront: Boolean(json['waaf']),
      volumeQuality: json['volume_quality'] ? this.getVolumeQuality(json['volume_quality']) : undefined,
      volumeUnit: json['volume_unit'],
      vatInfo: json['vat_info'] ? this.getVatInfo(json['vat_info']) : undefined,
      history: this.list(json['history']),
      paymentDeadline: json['payment_deadline'] ? moment.tz(json['payment_deadline'], moment.tz.guess()) : undefined,
      typology: json['typology'],
      isCurrent: json['is_current_state'],
      showFinalButton: json['show_final_button'],
    });

    invoice.paymentStatus = this.getPaymentStatus(json, invoice.paymentDeadline);

    return invoice;
  }

  private getVolumeQuality(json): InvoiceVolumeQuality {
    const {
      'realized(not_validated)': realizedNotValidated,
      'offtake_realized(not_validated)': offtakeRealizedNotValidated,
      'realized(validated)': realizedValidated,
      'offtake_realized(validated)': offtakeRealizedValidated,
      'offtake_realized(metering)': offtakeRealizedMetering,
      'offtake_realized(forecast)': offtakeRealizedForecast,
      'offtake_realized(declared)': offtakeRealizedDeclared,
      data_available: dataAvailable,
      fallbacked_to_forecast: fallbackedToForecast,
      fallbacked_to_declared: fallbackedToDeclared,
      filled_with_zero: filledWithZero,
      realized,
      offtakeRealized,
      declared,
      zero,
      ...others
    } = json;

    return {
      realizedValidated: realizedValidated || offtakeRealizedValidated,
      realizedNotValidated: realizedNotValidated || offtakeRealizedNotValidated,
      realized: realized || offtakeRealized,
      realizedMetering: offtakeRealizedMetering,
      realizedForecast: offtakeRealizedForecast,
      realizedDeclared: offtakeRealizedDeclared,
      dataAvailable,
      fallbackedToForecast,
      fallbackedToDeclared,
      filledWithZero,
      declared,
      zero,
      ...others,
    };
  }

  private getVatInfo(json): VatInfo {
    return {
      full: {
        amount: json['vat_full_amount'],
        applicableOnAmount: json['vat_full_applicable_on_amount'],
        exoneratedOnAmount: json['vat_full_exonerated_on_amount'],
        rate: json['vat_full_rate'],
      },
      reduced: {
        amount: json['vat_reduced_amount'],
        applicableOnAmount: json['vat_reduced_applicable_on_amount'],
        exoneratedOnAmount: json['vat_reduced_exonerated_on_amount'],
        rate: json['vat_reduced_rate'],
      },
      none: { amount: json['vat_none_applicable_on_amount'], rate: 0 },
    };
  }

  private getPaymentStatus(json, paymentDeadline): InvoicePaymentStatus {
    const paymentStatus = json['payment_status'];
    if (Utils.isNullOrUndefined(paymentStatus)) return;

    if (paymentStatus === 'paid') return InvoicePaymentStatus.paid;

    if (paymentDeadline) return moment().isAfter(paymentDeadline) ? InvoicePaymentStatus.overdue : InvoicePaymentStatus.unPaid;
  }
}
